import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { boxSizingStyles } from '../../styles/lit-shared';

export type UiButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

@customElement('ui-button')
export class UiButton extends LitElement {
  static styles = [
    boxSizingStyles,
    css`
      :host {
        display: inline-flex;
      }

      :host([fullwidth]) {
        display: flex;
        width: 100%;
      }

      button,
      a.btn {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-xs);
        width: 100%;
        min-height: 2.75rem;
        padding: 0.75rem 1.5rem;
        border: none;
        border-radius: var(--radius-sm);
        font-family: var(--font-sans);
        font-weight: 600;
        font-size: 0.9375rem;
        letter-spacing: 0.01em;
        line-height: 1.4;
        cursor: pointer;
        text-decoration: none;
        overflow: hidden;
        transition:
          transform var(--duration-normal) var(--ease-out),
          box-shadow var(--duration-normal) var(--ease-out),
          background var(--duration-normal) var(--ease-out),
          border-color var(--duration-normal) var(--ease-out),
          opacity var(--duration-fast) var(--ease-out);
      }

      button ::slotted(svg),
      a.btn ::slotted(svg),
      button ::slotted(img),
      a.btn ::slotted(img) {
        width: 1.125rem;
        height: 1.125rem;
        flex-shrink: 0;
      }

      button.primary,
      a.btn.primary {
        background: var(--gradient-primary);
        color: var(--palette-white);
        box-shadow: var(--shadow-glow-btn);
      }

      button.primary::before,
      a.btn.primary::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
          105deg,
          transparent 40%,
          rgb(255 255 255 / 18%) 50%,
          transparent 60%
        );
        transform: translateX(-100%);
        transition: transform var(--duration-slow) var(--ease-out);
      }

      button.primary:hover:not(:disabled),
      a.btn.primary:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-glow-btn-hover);
      }

      button.primary:hover:not(:disabled)::before,
      a.btn.primary:hover::before {
        transform: translateX(100%);
      }

      button.primary:active:not(:disabled),
      a.btn.primary:active {
        transform: translateY(0);
      }

      button.secondary,
      a.btn.secondary {
        background: var(--color-surface);
        color: var(--color-text);
        border: 1px solid var(--color-border-strong);
        box-shadow: var(--shadow-sm);
      }

      button.secondary:hover:not(:disabled),
      a.btn.secondary:hover {
        background: var(--color-surface-hover);
        border-color: var(--color-border-glow);
        transform: translateY(-1px);
      }

      button.ghost,
      a.btn.ghost {
        background: transparent;
        color: var(--color-primary-light);
        box-shadow: none;
      }

      button.ghost:hover:not(:disabled),
      a.btn.ghost:hover {
        background: rgb(139 92 246 / 10%);
      }

      button.danger {
        background: linear-gradient(135deg, #e11d48, #be123c);
        color: var(--palette-white);
        box-shadow: 0 0 20px rgb(225 29 72 / 25%);
      }

      button:disabled {
        opacity: 0.55;
        cursor: not-allowed;
        transform: none;
      }
    `,
  ];

  @property({ type: String }) variant: UiButtonVariant = 'primary';
  @property({ type: String }) type: 'button' | 'submit' | 'reset' = 'button';
  @property({ type: String }) href = '';
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true, attribute: 'fullwidth' }) fullWidth = false;

  private handleClick(e: Event) {
    if (this.disabled) return;

    this.dispatchEvent(
      new CustomEvent('ui-click', {
        bubbles: true,
        composed: true,
        detail: { originalEvent: e },
      }),
    );

    if (this.type !== 'submit') return;

    const form = this.closest('form');
    if (form) {
      e.preventDefault();
      form.requestSubmit();
    }
  }

  render() {
    if (this.href) {
      return html`
        <a
          class="btn ${this.variant}"
          href=${this.href}
          target=${this.href.startsWith('http') ? '_blank' : undefined}
          rel=${this.href.startsWith('http') ? 'noopener noreferrer' : undefined}
          @click=${this.handleClick}
        >
          <slot></slot>
        </a>
      `;
    }

    const isDisabled = this.disabled;

    return html`
      <button
        class=${this.variant}
        type=${this.type}
        ?disabled=${isDisabled}
        @click=${this.handleClick}
      >
        <slot></slot>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ui-button': UiButton;
  }
}
